import { supabase } from '../../../services/supabase'
import type { ListLoginEventsParams, LoginEventsPage, PlatformLoginEventType } from './loginHistory.types'
import { parseLoginEventsPage } from './loginHistory.utils'
import {
  buildLoginBlockedDedupId,
  buildLoginSuccessDedupId,
  clearLoginEventLogged,
  getLoginEventDedupTtlMs,
  tryClaimLoginEvent,
} from './loginHistory.utils'

const recordedLoginSessions = new Set<string>()
const inFlightLoginRecords = new Set<string>()

export async function listLoginEvents (params: ListLoginEventsParams = {}): Promise<LoginEventsPage> {
  const { data, error } = await supabase.rpc('sa_list_login_events', {
    p_user_id: params.userId ?? null,
    p_event_type: params.eventType ?? null,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
  })
  if (error) throw error
  return parseLoginEventsPage(data)
}

function readUserAgent (): string | null {
  if (typeof navigator === 'undefined') return null
  const agent = navigator.userAgent?.trim()
  return agent ? agent.slice(0, 512) : null
}

export async function recordPlatformLoginEvent (
  eventType: PlatformLoginEventType
): Promise<void> {
  const { error } = await supabase.rpc('record_my_platform_login_event', {
    p_event_type: eventType,
    p_user_agent: readUserAgent(),
  })
  if (error) throw error
}

export interface RecordPlatformLoginEventOptions {
  userId: string
  accessToken?: string | null
}

export async function recordPlatformLoginEventOnce (
  eventType: PlatformLoginEventType,
  options: RecordPlatformLoginEventOptions
): Promise<void> {
  const dedupId = eventType === 'login_success'
    ? (options.accessToken
      ? buildLoginSuccessDedupId(options.userId, options.accessToken)
      : null)
    : buildLoginBlockedDedupId(options.userId)

  if (!dedupId) return

  const ttlMs = getLoginEventDedupTtlMs(eventType)
  const flightKey = `${eventType}:${dedupId}`

  if (eventType === 'login_success' && recordedLoginSessions.has(flightKey)) return
  if (inFlightLoginRecords.has(flightKey)) return
  if (!tryClaimLoginEvent(eventType, dedupId, ttlMs)) return

  inFlightLoginRecords.add(flightKey)
  try {
    await recordPlatformLoginEvent(eventType)
    if (eventType === 'login_success') {
      recordedLoginSessions.add(flightKey)
    }
  } catch (error) {
    clearLoginEventLogged(eventType, dedupId)
    throw error
  } finally {
    inFlightLoginRecords.delete(flightKey)
  }
}
